/**
 * #sms channel — SMS inbox + compose for the owner's SIM.
 *
 * Rows land in `channel_messages` via the POST /api/webhook/sms-mirror
 * endpoint and carry phone + direction in `sms_meta_json`.
 *
 * Default view: only messages where direction=inbound AND to_phone matches
 * the configured SIM number, EXCLUDING self-loops (from==to) and synthetic
 * test senders. User can toggle any of those filters in the header.
 *
 * Compose box sends via POST /api/sms/send which proxies to hub-cell.
 */

import { get, post, API_BASE } from '../api';
import { state } from '../state';
import { realtime, channelTopic, type RealtimeEvent } from '../realtime';

interface SmsMeta {
  from_phone?: string;
  to_phone?: string;
  direction?: 'inbound' | 'outbound';
  received_at?: string;
}

interface SmsRow {
  id: string;
  server_id: string;
  channel: string;
  content: string;
  created_at: string;
  sms_meta_json?: string | null;
  /** JSON-stringified array of `{id,filename,content_type,size,url}`
   *  populated by the MMS mirror webhook (batch 6a feature 3). */
  attachments?: string | null;
}

interface SmsAttachment {
  id?: string;
  filename?: string;
  content_type?: string;
  size?: number;
  url?: string;
}

function parseAttachments(raw: string | null | undefined): SmsAttachment[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw) as unknown;
    if (Array.isArray(v)) return v as SmsAttachment[];
  } catch {
    /* ignore */
  }
  return [];
}

function renderAttachment(a: SmsAttachment): string {
  const url = a.url || '';
  const mime = (a.content_type || '').toLowerCase();
  const name = a.filename || 'attachment';
  if (!url) return '';
  if (mime.startsWith('image/')) {
    return `
      <a href="${escape(url)}" target="_blank" rel="noopener noreferrer" style="display:inline-block;margin:6px 6px 0 0">
        <img src="${escape(url)}" alt="${escape(name)}" style="max-width:260px;max-height:260px;border-radius:6px;border:1px solid var(--border);display:block" loading="lazy">
      </a>
    `;
  }
  if (mime.startsWith('video/')) {
    return `
      <div style="margin:6px 0">
        <video controls preload="metadata" style="max-width:320px;max-height:240px;border-radius:6px;border:1px solid var(--border);display:block">
          <source src="${escape(url)}" type="${escape(mime)}">
        </video>
      </div>
    `;
  }
  if (mime.startsWith('audio/')) {
    return `
      <div style="margin:6px 0">
        <audio controls preload="metadata" style="max-width:320px;display:block">
          <source src="${escape(url)}" type="${escape(mime)}">
        </audio>
        <div style="font-size:11px;color:var(--fg-muted);margin-top:2px">${escape(name)}</div>
      </div>
    `;
  }
  // Fallback: plain download link so octet-stream and unknown types still
  // surface to the user (carrier-compat audit: never silently drop a part).
  const clipIcon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:text-bottom;margin-right:4px"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 17.93 8.83L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>`;
  return `
    <div style="margin:6px 0">
      <a href="${escape(url)}" target="_blank" rel="noopener noreferrer" style="color:var(--accent);font-size:13px;text-decoration:none;display:inline-flex;align-items:center">
        ${clipIcon}${escape(name)}${typeof a.size === 'number' ? ` (${Math.round(a.size / 1024)} kB)` : ''}
      </a>
    </div>
  `;
}

/** Normalise phone numbers so +61498108142 / 0498108142 / 61498108142 all match. */
function normalisePhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/[^0-9]/g, '');
  // AU: strip leading 61, strip leading 0 — compare by last 9 digits of a mobile.
  if (digits.length >= 9) return digits.slice(-9);
  return digits;
}

/** The SIM this inbox is for. TODO: pull from a per-user setting once we have one. */
const SIM_PHONE = '+61498108142';
const SIM_PHONE_NORM = normalisePhone(SIM_PHONE);

/** Synthetic probe senders the mirror pipeline uses to self-test. Hidden by default. */
const TEST_SENDER_PREFIXES = ['+61400000', '0400000'];

interface InboxPrefs {
  showTests: boolean;
  showOutbound: boolean;
}

const PREFS_KEY = 'hub-people:sms-inbox-prefs';

function loadPrefs(): InboxPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<InboxPrefs>;
      return {
        showTests: p.showTests === true,
        showOutbound: p.showOutbound === true,
      };
    }
  } catch {
    /* ignore */
  }
  return { showTests: false, showOutbound: false };
}

function savePrefs(prefs: InboxPrefs): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

let unsubRealtime: (() => void) | null = null;
let currentTopic: string | null = null;
let allRows: SmsRow[] = [];
let prefs: InboxPrefs = loadPrefs();

export function cleanupSms(): void {
  if (unsubRealtime) { unsubRealtime(); unsubRealtime = null; }
  if (currentTopic) { realtime.unsubscribeChannel(currentTopic); currentTopic = null; }
}

function parseMeta(raw: string | null | undefined): SmsMeta {
  if (!raw) return {};
  try { return JSON.parse(raw) as SmsMeta; } catch { return {}; }
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, c => (
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;'
  ));
}

/** Return true if the row passes the user's current filters. */
function passesFilter(row: SmsRow): boolean {
  const meta = parseMeta(row.sms_meta_json);
  const direction = meta.direction || 'inbound';
  const fromN = normalisePhone(meta.from_phone);
  const toN = normalisePhone(meta.to_phone);

  // Direction: default inbound-only; toggle shows outbound too.
  if (!prefs.showOutbound && direction !== 'inbound') return false;

  // Must involve the SIM number on at least one side.
  if (toN !== SIM_PHONE_NORM && fromN !== SIM_PHONE_NORM) return false;

  // Drop self-loops — same phone on both sides is the modem echoing its own send.
  if (fromN && toN && fromN === toN) return false;

  // Test senders off by default.
  if (!prefs.showTests) {
    const raw = meta.from_phone || '';
    if (TEST_SENDER_PREFIXES.some(p => raw.startsWith(p))) return false;
  }

  return true;
}

function prettyTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
  const daysAgo = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
  if (daysAgo < 7) {
    return d.toLocaleDateString(undefined, { weekday: 'short' }) + ' ' +
      d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString();
}

function renderRow(row: SmsRow): string {
  const meta = parseMeta(row.sms_meta_json);
  const direction = meta.direction || 'inbound';
  const inbound = direction === 'inbound';
  const counterparty = inbound ? (meta.from_phone || 'unknown') : (meta.to_phone || 'unknown');
  const when = meta.received_at || row.created_at || '';
  const body = row.content || '';
  const dirLabel = inbound ? 'in' : 'out';
  const dirColor = inbound ? 'var(--accent)' : 'var(--fg-muted)';
  const attachments = parseAttachments(row.attachments);
  const attachmentsHtml = attachments.length
    ? `<div style="margin-top:6px;display:flex;flex-wrap:wrap;gap:0">${attachments.map(renderAttachment).join('')}</div>`
    : '';
  const isMms = attachments.length > 0;

  return `
    <div class="sms-row" data-id="${escape(row.id)}" style="padding:12px 16px;border-bottom:1px solid var(--border);font-family:var(--font-sans);cursor:default">
      <div style="display:flex;gap:8px;align-items:baseline;margin-bottom:4px">
        <span style="display:inline-block;padding:1px 6px;border-radius:3px;background:${dirColor};color:var(--bg);font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.3px">${dirLabel}</span>
        ${isMms ? `<span style="display:inline-block;padding:1px 6px;border-radius:3px;background:var(--bg-elev);color:var(--fg-muted);font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.3px;border:1px solid var(--border)">MMS</span>` : ''}
        <span style="font-weight:600;color:var(--fg);font-size:14px">${escape(counterparty)}</span>
        <span style="margin-left:auto;color:var(--fg-muted);font-size:12px">${escape(prettyTime(when))}</span>
      </div>
      <div style="white-space:pre-wrap;line-height:1.4;color:var(--fg);font-size:14px">${escape(body)}</div>
      ${attachmentsHtml}
    </div>
  `;
}

function renderList(listEl: HTMLElement, countEl: HTMLElement | null): void {
  const filtered = allRows.filter(passesFilter);
  // Newest first — normal inbox convention.
  filtered.sort((a, b) => {
    const at = parseMeta(a.sms_meta_json).received_at || a.created_at || '';
    const bt = parseMeta(b.sms_meta_json).received_at || b.created_at || '';
    return bt.localeCompare(at);
  });
  if (filtered.length === 0) {
    listEl.innerHTML = `
      <div style="padding:40px 20px;text-align:center;color:var(--fg-muted)">
        <div style="font-size:18px;margin-bottom:8px">No inbound SMS yet</div>
        <div style="font-size:13px">Messages to <code>${escape(SIM_PHONE)}</code> will appear here.</div>
      </div>
    `;
  } else {
    listEl.innerHTML = filtered.map(renderRow).join('');
  }
  if (countEl) countEl.textContent = String(filtered.length);
}

export async function renderSms(container: HTMLElement): Promise<void> {
  cleanupSms();

  const sid = state.currentServer;
  if (!sid) {
    container.innerHTML = '<div style="padding:20px;color:var(--fg-muted)">Select a server to view SMS.</div>';
    return;
  }

  container.innerHTML = `
    <div style="flex:1;min-height:0;display:flex;flex-direction:column">
      <div style="padding:10px 16px;border-bottom:1px solid var(--border);display:flex;gap:12px;align-items:center;flex-wrap:wrap;font-family:var(--font-sans);font-size:13px">
        <div style="font-weight:600;color:var(--fg)">Inbox for ${escape(SIM_PHONE)}</div>
        <div style="color:var(--fg-muted)">
          showing <span id="sms-count" style="font-weight:600;color:var(--fg)">0</span>
        </div>
        <label style="margin-left:auto;display:inline-flex;align-items:center;gap:6px;cursor:pointer;color:var(--fg-muted);user-select:none">
          <input id="sms-show-tests" type="checkbox" ${prefs.showTests ? 'checked' : ''} style="margin:0">
          Show test senders
        </label>
        <label style="display:inline-flex;align-items:center;gap:6px;cursor:pointer;color:var(--fg-muted);user-select:none">
          <input id="sms-show-outbound" type="checkbox" ${prefs.showOutbound ? 'checked' : ''} style="margin:0">
          Show outbound
        </label>
      </div>
      <div id="sms-list" style="flex:1;overflow-y:auto"></div>
      <div id="sms-compose" style="padding:10px 16px;border-top:1px solid var(--border);display:flex;gap:8px;align-items:flex-end">
        <input id="sms-to" type="tel" placeholder="Phone number" style="width:140px;padding:8px 10px;border:1px solid var(--border);border-radius:6px;background:var(--bg-elev);color:var(--fg);font-size:13px;font-family:var(--font-sans);outline:none" />
        <textarea id="sms-body" placeholder="Message" rows="1" style="flex:1;padding:8px 10px;border:1px solid var(--border);border-radius:6px;background:var(--bg-elev);color:var(--fg);font-size:13px;font-family:var(--font-sans);resize:none;outline:none;min-height:36px;max-height:120px;line-height:1.4"></textarea>
        <button id="sms-send-btn" style="padding:8px 16px;border:none;border-radius:6px;background:var(--accent);color:var(--bg);font-size:13px;font-weight:600;cursor:pointer;white-space:nowrap;font-family:var(--font-sans)">Send</button>
      </div>
    </div>
  `;

  const listEl = container.querySelector<HTMLElement>('#sms-list');
  const countEl = container.querySelector<HTMLElement>('#sms-count');
  const showTestsEl = container.querySelector<HTMLInputElement>('#sms-show-tests');
  const showOutboundEl = container.querySelector<HTMLInputElement>('#sms-show-outbound');
  if (!listEl) return;

  const rerender = (): void => renderList(listEl, countEl);

  showTestsEl?.addEventListener('change', () => {
    prefs = { ...prefs, showTests: showTestsEl.checked };
    savePrefs(prefs);
    rerender();
  });
  showOutboundEl?.addEventListener('change', () => {
    prefs = { ...prefs, showOutbound: showOutboundEl.checked };
    savePrefs(prefs);
    rerender();
  });

  // ── Compose box ──────────────────────────────────────────────────
  const toEl = container.querySelector<HTMLInputElement>('#sms-to');
  const bodyEl = container.querySelector<HTMLTextAreaElement>('#sms-body');
  const sendBtn = container.querySelector<HTMLButtonElement>('#sms-send-btn');

  bodyEl?.addEventListener('input', () => {
    if (bodyEl) {
      bodyEl.style.height = 'auto';
      bodyEl.style.height = Math.min(bodyEl.scrollHeight, 120) + 'px';
    }
  });

  const doSend = async (): Promise<void> => {
    if (!toEl || !bodyEl || !sendBtn) return;
    const number = toEl.value.trim();
    const body = bodyEl.value.trim();
    if (!number || !body) return;

    sendBtn.disabled = true;
    sendBtn.textContent = '...';
    try {
      await post<{ sent: boolean }>(`${API_BASE}/api/sms/send`, { to: number, body });
      bodyEl.value = '';
      bodyEl.style.height = 'auto';
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(`SMS send failed: ${msg}`);
    } finally {
      sendBtn.disabled = false;
      sendBtn.textContent = 'Send';
    }
  };

  sendBtn?.addEventListener('click', doSend);
  bodyEl?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      doSend();
    }
  });

  try {
    // Fetch a deep page so the filter has the full history to work with. 1057 rows
    // today ≈ small; 2000 cap keeps the payload bounded.
    allRows = await get<SmsRow[]>(
      `${API_BASE}/api/messages?server_id=${encodeURIComponent(sid)}&channel=sms&limit=2000`,
    );
    rerender();
  } catch (e) {
    listEl.innerHTML = `<div style="padding:20px;color:var(--danger)">Failed to load SMS: ${escape(String(e))}</div>`;
    return;
  }

  const topic = channelTopic(sid, 'sms');
  currentTopic = topic;
  realtime.connect();
  realtime.subscribeChannel(topic);
  unsubRealtime = realtime.on((ev: RealtimeEvent) => {
    if (ev.type !== 'message_created' || ev.channel_id !== topic) return;
    const msg = ev.message as unknown as SmsRow;
    // Dedup: if we already have this id, skip.
    if (allRows.some(r => r.id === msg.id)) return;
    allRows.unshift(msg);
    rerender();
  });
}
